import { useState } from 'react';
import { Bot, BotOff, Eye, EyeOff } from 'lucide-react';
import clsx from 'clsx';

export interface FollowAIToggleProps {
  isFollowingAI: boolean;
  onToggle: (enabled: boolean) => void;
  currentAIFile?: string;
  isAIGenerating?: boolean;
  className?: string;
}

export function FollowAIToggle({
  isFollowingAI,
  onToggle,
  currentAIFile,
  isAIGenerating = false,
  className
}: FollowAIToggleProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleToggle = () => {
    onToggle(!isFollowingAI);
  };

  const getTooltipText = () => {
    if (isFollowingAI) {
      return isAIGenerating 
        ? `Following AI (currently editing: ${currentAIFile || 'unknown file'})`
        : 'Following AI - will auto-switch to files being edited by AI';
    } else {
      return 'Not following AI - you can work on different files while AI generates code';
    }
  };

  return (
    <div className={clsx('relative', className)}>
      <button
        onClick={handleToggle}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className={clsx(
          'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-200',
          {
            'bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700 dark:hover:bg-blue-900/50': isFollowingAI,
            'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700 dark:hover:bg-gray-700': !isFollowingAI,
            'animate-pulse': isAIGenerating && isFollowingAI,
          }
        )}
        title={getTooltipText()}
      >
        {/* Icon */}
        <div className="relative">
          {isFollowingAI ? (
            <Bot className={clsx('size-4', {
              'text-blue-600 dark:text-blue-400': isFollowingAI,
              'animate-pulse': isAIGenerating
            })} />
          ) : (
            <BotOff className="size-4" />
          )}
          
          {/* AI Activity Indicator */}
          {isAIGenerating && isFollowingAI && (
            <div className="absolute -top-1 -right-1 size-2 bg-green-500 rounded-full animate-pulse" />
          )}
        </div>

        {/* Label */}
        <span>
          Follow AI
        </span>

        {/* Status Indicator */}
        <div className="flex items-center">
          {isFollowingAI ? (
            <Eye className="size-3 opacity-70" />
          ) : (
            <EyeOff className="size-3 opacity-70" />
          )}
        </div>
      </button>

      {/* Tooltip */}
      {showTooltip && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap z-50 dark:bg-gray-700">
          {getTooltipText()}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
        </div>
      )}

      {/* Current AI File Indicator */}
      {isAIGenerating && currentAIFile && isFollowingAI && (
        <div className="absolute top-full left-0 mt-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded border border-blue-200 whitespace-nowrap dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700">
          AI editing: {currentAIFile.split('/').pop()}
        </div>
      )}
    </div>
  );
}

/**
 * Hook for managing Follow AI state and behavior
 */
export function useFollowAI() {
  const [isFollowingAI, setIsFollowingAI] = useState(true); // ON by default
  const [currentAIFile, setCurrentAIFile] = useState<string | undefined>();
  const [isAIGenerating, setIsAIGenerating] = useState(false);

  // Toggle Follow AI mode
  const toggleFollowAI = (enabled?: boolean) => {
    setIsFollowingAI(enabled !== undefined ? enabled : !isFollowingAI);
  };

  // Update current AI file being edited
  const updateCurrentAIFile = (filePath?: string) => {
    setCurrentAIFile(filePath);
  };

  // Update AI generation status
  const updateAIGenerationStatus = (generating: boolean) => {
    setIsAIGenerating(generating);
    if (!generating) {
      setCurrentAIFile(undefined);
    }
  };

  // Auto-follow AI file changes (when Follow AI is enabled)
  const shouldFollowToFile = (filePath: string) => {
    return isFollowingAI && isAIGenerating && filePath === currentAIFile;
  };

  // Get visual indicator for AI activity
  const getAIActivityIndicator = () => {
    if (!isAIGenerating) return null;
    
    return {
      isActive: true,
      currentFile: currentAIFile,
      isFollowing: isFollowingAI
    };
  };

  return {
    isFollowingAI,
    currentAIFile,
    isAIGenerating,
    toggleFollowAI,
    updateCurrentAIFile,
    updateAIGenerationStatus,
    shouldFollowToFile,
    getAIActivityIndicator
  };
}

/**
 * AI File Indicator Component - shows which file AI is currently working on
 */
export interface AIFileIndicatorProps {
  currentFile?: string;
  isVisible: boolean;
  className?: string;
}

export function AIFileIndicator({ 
  currentFile, 
  isVisible, 
  className 
}: AIFileIndicatorProps) {
  if (!isVisible || !currentFile) return null;

  return (
    <div className={clsx(
      'flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 text-sm rounded-md border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-700',
      className
    )}>
      <div className="flex items-center gap-1">
        <Bot className="size-4" />
        <div className="size-2 bg-green-500 rounded-full animate-pulse" />
      </div>
      <span className="text-xs">
        AI editing: <span className="font-medium">{currentFile.split('/').pop()}</span>
      </span>
    </div>
  );
}

/**
 * Utility function to extract file name from path
 */
export function getFileName(filePath: string): string {
  return filePath.split('/').pop() || filePath;
}

/**
 * Utility function to check if AI should auto-switch to a file
 */
export function shouldAutoSwitchToFile(
  isFollowingAI: boolean,
  isAIGenerating: boolean,
  targetFile: string,
  currentAIFile?: string
): boolean {
  return isFollowingAI && isAIGenerating && targetFile === currentAIFile;
}
